<script setup lang="ts">
import { computed } from 'vue';
import CardItem from '@/components/card-item.vue';
import type { EditLine } from '@/api/types/orderEdit';
import { useOrderEditStore } from '@/stores/orderEdit';
import { fillText, lockText, useEditTexts } from '@/composables/useEditTexts';
import { firstQuantity, formatCents, isLineEditable } from '@/services/orderEditRules';

// One order line on card-item.vue's stepper. The store clamps every change
// to the server's bounds; this only shows them.
const { line, frozen = false } = defineProps<{ line: EditLine; frozen?: boolean }>();

const store = useOrderEditStore();
const t = useEditTexts();

const target = computed(() => store.targetOf(line.product));
const editable = computed(() => !frozen && isLineEditable(line) && store.unlockedOf(line));
const max = computed(() => store.maxOf(line));
const maxValue = computed(() => (Number.isFinite(max.value) ? max.value : undefined));
const price = computed(() => (line.unitCents === null ? undefined : formatCents(line.unitCents)));
const note = computed(() =>
  target.value !== line.current ? fillText(t.edit_line_current, { count: line.current }) : undefined,
);
// A meal line locked like its section: the section says it once.
const description = computed(() =>
  !store.unlockedOf(line)
    ? fillText(t.edit_offered_from, { count: line.offeredFrom ?? 0 })
    : line.category === 'meal' && line.lock !== null && line.lock === store.view?.menusLock
      ? ''
      : (lockText(t, line.lock) ?? ''),
);

function onUpdate(quantity: number) {
  store.setTarget(line.product, quantity);
}

function onClick() {
  if (editable.value && target.value === 0) store.addLine(line.product);
}
</script>

<template>
  <card-item
    data-testid="order-edit-line"
    :data-product="line.product"
    :data-lock="line.lock ?? ''"
    :title="line.label"
    :description="description"
    :picture-url="line.photo ?? undefined"
    :price="price"
    :price-note="note"
    :model-value="target"
    :is-selected="target > 0"
    :has-quantity="true"
    :editable-quantity="editable"
    :can-add="editable && max > 0"
    :can-delete="editable && line.min === 0"
    :min-value="firstQuantity(line)"
    :max-value="maxValue"
    :disabled="!editable"
    @update:model-value="onUpdate"
    @click="onClick"
  />
</template>
